import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone } from 'lucide-react';
import { AppStoreButtons } from './AppStoreButtons';
import { useLanguage } from '../../contexts/LanguageContext';

export function MarketingFooter() {
  const { t } = useLanguage();

  const columns = [
    {
      title: t('footer.column.allure'),
      key: 'allure',
      links: [
        { to: '/welcome', label: t('footer.link.home'), key: 'home' },
        { to: '/about', label: t('footer.link.about'), key: 'about' },
        { to: '/contact', label: t('footer.link.contact'), key: 'contact' },
      ],
    },
    {
      title: t('footer.column.help'),
      key: 'help',
      links: [
        { to: '/support', label: t('footer.link.support'), key: 'support' },
        { to: '/privacy', label: t('footer.link.privacy'), key: 'privacy' },
        { to: '/terms', label: t('footer.link.terms'), key: 'terms' },
        { to: '/legal/imprint', label: t('footer.link.imprint'), key: 'imprint' },
      ],
    },
  ];

  return (
    <footer
      data-testid="marketing-footer"
      style={{
        background: '#0E0A1E',
        color: '#E8E5F3',
        marginTop: 80,
        paddingTop: 64,
        paddingBottom: 36,
      }}
    >
      <div style={{ maxWidth: 1180, margin: '0 auto', padding: '0 22px' }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: 40,
            paddingBottom: 44,
            borderBottom: '1px solid rgba(255,255,255,0.10)',
          }}
        >
          {/* Brand block */}
          <div data-testid="marketing-footer-brand">
            <Link to="/welcome" style={{ display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none', color: '#FFFFFF' }}>
              <img src="/allure-logo.png" alt="Allure" style={{ width: 42, height: 42, objectFit: 'contain' }} />
              <span style={{ fontFamily: "'Inria Serif', serif", fontSize: 24, fontWeight: 700, color: '#FFFFFF' }}>Allure</span>
            </Link>
            <p style={{ margin: '18px 0 0', fontFamily: "'Manrope', sans-serif", fontSize: 14, lineHeight: 1.6, color: '#B1A7CC', maxWidth: 280 }}>
              {t('footer.brandTagline')}
            </p>
          </div>

          {/* Nav columns */}
          {columns.map((col) => (
            <div key={col.key} data-testid={`marketing-footer-column-${col.key}`}>
              <h4 style={{ margin: 0, fontFamily: "'Manrope', sans-serif", fontSize: 12, letterSpacing: 1.2, textTransform: 'uppercase', color: '#9D93B8', fontWeight: 700 }}>
                {col.title}
              </h4>
              <ul style={{ listStyle: 'none', padding: 0, margin: '18px 0 0', display: 'flex', flexDirection: 'column', gap: 12 }}>
                {col.links.map((link) => (
                  <li key={link.key}>
                    <Link
                      to={link.to}
                      data-testid={`marketing-footer-link-${link.key}`}
                      style={{ color: '#E8E5F3', textDecoration: 'none', fontFamily: "'Manrope', sans-serif", fontSize: 14, fontWeight: 500 }}
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}

          {/* Contact column */}
          <div data-testid="marketing-footer-contact">
            <h4 style={{ margin: 0, fontFamily: "'Manrope', sans-serif", fontSize: 12, letterSpacing: 1.2, textTransform: 'uppercase', color: '#9D93B8', fontWeight: 700 }}>
              {t('footer.getInTouch')}
            </h4>
            <ul style={{ listStyle: 'none', padding: 0, margin: '18px 0 0', display: 'flex', flexDirection: 'column', gap: 12 }}>
              <li>
                <a
                  href="mailto:contact@allurexp.com"
                  data-testid="marketing-footer-email"
                  style={{ color: '#FFFFFF', textDecoration: 'none', fontFamily: "'Manrope', sans-serif", fontSize: 14, fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: 10 }}
                >
                  <Mail size={14} color="#E53935" /> contact@allurexp.com
                </a>
              </li>
              <li>
                <a
                  href="tel:+306943963295"
                  data-testid="marketing-footer-phone-1"
                  style={{ color: '#E8E5F3', textDecoration: 'none', fontFamily: "'Manrope', sans-serif", fontSize: 14, fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: 10 }}
                >
                  <Phone size={14} color="#E53935" /> +30 694 396 3295
                </a>
              </li>
              <li>
                <a
                  href="tel:+306943963291"
                  data-testid="marketing-footer-phone-2"
                  style={{ color: '#E8E5F3', textDecoration: 'none', fontFamily: "'Manrope', sans-serif", fontSize: 14, fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: 10 }}
                >
                  <Phone size={14} color="#E53935" /> +30 694 396 3291
                </a>
              </li>
            </ul>
          </div>

          {/* Download column */}
          <div data-testid="marketing-footer-download">
            <h4 style={{ margin: 0, fontFamily: "'Manrope', sans-serif", fontSize: 12, letterSpacing: 1.2, textTransform: 'uppercase', color: '#9D93B8', fontWeight: 700 }}>
              {t('footer.getTheApp')}
            </h4>
            <div style={{ marginTop: 18 }}>
              <AppStoreButtons variant="dark" layout="column" dataTestPrefix="marketing-footer-store" />
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, justifyContent: 'space-between', alignItems: 'center', paddingTop: 24 }}>
          <p style={{ margin: 0, fontFamily: "'Manrope', sans-serif", fontSize: 12, color: '#7E7396' }}>
            © {new Date().getFullYear()} ALLURE EXPERIENCE P.C. — Γ.Ε.ΜΗ. 194817609000 — ΑΦΜ EL803335274. {t('footer.rights')}
          </p>
          <p style={{ margin: 0, fontFamily: "'Manrope', sans-serif", fontSize: 12, color: '#7E7396' }}>
            {t('footer.crafted')}
          </p>
        </div>
      </div>
    </footer>
  );
}
